import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import {
  createCommandSchedule,
  editCommandSchedule,
  loadCommandSchedule,
  loadCommandSchedules,
  loadUnitCommands,
  removeCommandSchedule,
} from './api';
import { CommandScheduleSchema } from './schema';

const commandScheduleEntityAdapter = createEntityAdapter();

const commandSlice = createSlice({
  name: 'commands',
  initialState: {
    commandSchedules: commandScheduleEntityAdapter.getInitialState({}),
    // Per-site command catalog cache: { [siteId]: { loading, error, data } }.
    availableCommands: {},
  },
  reducers: {
    // Drop a site's cached catalog so the next load re-fetches (e.g. after a
    // command that can change availability, like a firmware upgrade).
    invalidateAvailableCommands(state, action) {
      delete state.availableCommands[action.payload];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadCommandSchedules.fulfilled, (state, action) => {
        const { entities } = normalize(action.payload.results, [CommandScheduleSchema]);
        commandScheduleEntityAdapter.upsertMany(state.commandSchedules, entities.commandSchedules ?? {});
      })
      .addCase(loadCommandSchedule.fulfilled, (state, action) => {
        const { entities, result } = normalize(action.payload, CommandScheduleSchema);
        const commandSchedule = entities.commandSchedules[result];
        commandScheduleEntityAdapter.upsertOne(state.commandSchedules, commandSchedule);
      })
      .addCase(createCommandSchedule.fulfilled, (state, action) => {
        const { entities, result } = normalize(action.payload, CommandScheduleSchema);
        const commandSchedule = entities.commandSchedules[result];
        commandScheduleEntityAdapter.upsertOne(state.commandSchedules, commandSchedule);
      })
      .addCase(editCommandSchedule.fulfilled, (state, action) => {
        const { entities, result } = normalize(action.payload, CommandScheduleSchema);
        const commandSchedule = entities.commandSchedules[result];
        commandScheduleEntityAdapter.upsertOne(state.commandSchedules, commandSchedule);
      })
      .addCase(removeCommandSchedule.fulfilled, (state, action) => {
        const removedId = action.payload;
        commandScheduleEntityAdapter.removeOne(state.commandSchedules, removedId);
      })
      .addCase(loadUnitCommands.pending, (state, action) => {
        const siteId = action.meta.arg;
        state.availableCommands[siteId] = { ...(state.availableCommands[siteId] || {}), loading: true, error: null };
      })
      .addCase(loadUnitCommands.fulfilled, (state, action) => {
        state.availableCommands[action.meta.arg] = { loading: false, error: null, data: action.payload };
      })
      .addCase(loadUnitCommands.rejected, (state, action) => {
        const siteId = action.meta.arg;
        state.availableCommands[siteId] = {
          ...(state.availableCommands[siteId] || {}),
          loading: false,
          error: action.payload ?? true,
        };
      });
  },
});

export const { invalidateAvailableCommands } = commandSlice.actions;

// Cached command catalog entry for a site: { loading, error, data } | undefined.
export const selectAvailableCommands = (state, siteId) => state.commands.availableCommands?.[siteId];

export const selectCommandScheduleEntities = state => {
  return state.commands.commandSchedules;
};

// Command Schedule Selectors

export const { selectById: selectCommandScheduleById, selectAll: selectAllCommandSchedules } =
  commandScheduleEntityAdapter.getSelectors(selectCommandScheduleEntities);

export const selectCommandSchedulesBySiteId = (state, siteId) => {
  const allSchedules = selectAllCommandSchedules(state);
  return allSchedules.filter(cs => cs.site == siteId); // loose equality for string/number
};

const { reducer } = commandSlice;

export default reducer;
